<template>
  <VDialogV3
    id="bank-account-card"
    position="right"
    width="600px"

    :entity="localBankAccount"
    :submit-disabled="localBankAccount.isExisting ? 'Расчётный счёт нельзя редактировать' : null"
    :submit-method="submit"
  >
    <section>
      <VSelect
        ref="company"
        v-model="localBankAccount.company"
        :disabled="localBankAccount.isExisting"
        required
        label="Юр. лицо"
        layout="vertical"
        :options="partner?.companies"
      />
    </section>

    <section>
      <VFormItem
        label="Расчётный счёт"
        layout="vertical"
        with-asterisk
      >
        <VInputMask
          ref="accountInput"
          :value="localBankAccount.account"
          :mask="localBankAccount.getAccountMask()"
          required
          :validate-method="() => localBankAccount.validateAccountNumber()"
          :disabled="localBankAccount.isExisting"
          @input="localBankAccount.account = $event"
        />

        <VSelect
          :value="currencyCode"
          :options="currencies"
          option-label="symbol"
          option-value="code"
          :disabled="localBankAccount.isExisting"
          @input="onInputCurrencyCode"
        />
      </VFormItem>

      <VInputMask
        ref="rcbicInput"
        :label="localBankAccount.getBankIdLabel()"
        layout="vertical"
        :mask="localBankAccount.getBankIdMask()"
        required
        :value="localBankAccount.bank.id"
        :disabled="localBankAccount.isExisting"
        @input="onBankIdInput"
      />

      <VInput
        ref="swift"
        v-model="localBankAccount.swift"
        label="SWIFT"
        layout="vertical"
        :disabled="localBankAccount.isExisting"
        :validate-method="(value) => localBankAccount.validateSwift(value)"
      />
    </section>

    <!-- реквизиты банка -->
    <section v-if="localBankAccount.bank?.name">
      <VInput
        label="Наименование банка"
        layout="vertical"
        :value="localBankAccount.bank.name"
        disabled
      />
      <VInput
        label="Корреспондентский счёт"
        layout="vertical"
        :value="localBankAccount.bank.account"
        :disabled="localBankAccount.isExisting"
      />
      <VInput
        label="Налоговый номер"
        layout="vertical"
        :value="localBankAccount.bank.tin"
        disabled
      />
      <VTextarea
        v-model="localBankAccount.bank.address.stringRepresentation"
        label="Адрес"
        layout="vertical"
        disabled
      />
      <VTextarea
        v-model="localBankAccount.bank.address.cityName"
        label="Город"
        layout="vertical"
        disabled
      />
    </section>

    <section v-if="localBankAccount.isExisting === false">
      <VHelperText icon="attention">
        Пожалуйста, будьте внимательны при заполнении.<br>
        После создания счёта его редактирование невозможно, но вы можете воспользоваться функцией удаления.
      </VHelperText>
    </section>
  </VDialogV3>
</template>

<script lang="ts">

import BankAccount         from "@/domains/BankAccounts/BankAccount";
import BankAccountsService from "@/domains/BankAccounts/BankAccountsService";
import CurrenciesService   from "@/domains/Currencies/CurrenciesService";
import BanksService        from "@/domains/Banks/BanksService";
import Partner             from "@/domains/Partners/Partner";
import Company             from "@/domains/Companies/Company";
import type Country        from "@/domains/Countries/Country";

export default defineComponent({
  name: 'BankAccountCard',

  props: {
    bankAccount: {
      type:    BankAccount,
      default: null,
    },
    partner: {
      type:     Partner,
      required: true,
    },
    /** Юр. лицо, к которому сразу привязать новый счёт */
    company: {
      type:    Company,
      default: null,
    },
  },

  data() {
    let localBankAccount: BankAccount;

    if (this.bankAccount === null) {
      localBankAccount = BankAccount.fromPartner(this.partner);

      if (this.company !== null) {
        localBankAccount.company = this.company;
      }
    }
    else {
      localBankAccount = this.$core.utils.cloneDeep(this.bankAccount) as BankAccount;

      /** компания с сервера содержит только id, нужен хотя бы ещё name для отображения при открытии */
      const fullCompany = this.partner.companies.find(company => company.id === localBankAccount.company.id);

      if (fullCompany !== undefined) {
        localBankAccount.company = this.$core.utils.cloneDeep(fullCompany);
      }

      /** назначаем валюту как у партнёра */
      if (this.$core.utils.isNullOrUndefined(localBankAccount.company) === false) {
        localBankAccount.company.country = { code: this.partner.country.code } as Country;
      }
    }

    return {
      localBankAccount,
    };
  },

  computed: {
    currencies() {
      return Array.from(this.$core.currencies.currencies.values());
    },

    currencyCode: {
      get() {
        return CurrenciesService.currencies.get(this.localBankAccount.currencyCode);
      },
      set(value) {
        this.localBankAccount.currencyCode = value;
      },
    },
  },

  methods: {
    onInputCurrencyCode(value) {
      this.currencyCode = value.code;
    },

    async onBankIdInput(value) {
      const id = typeof value === 'string' ? value : value?.raw ?? '';

      this.localBankAccount.bank.id = id;

      const completeLength = this.localBankAccount.getBankIdCompleteLength();

      if (completeLength !== null && id.length === completeLength) {
        const bank = await BanksService.getBank(this.localBankAccount.countryCode, id);

        if (this.$core.utils.isNullOrUndefined(bank) === false) {
          this.localBankAccount.bank = bank;
        }
      }
    },

    /**
     * Сабмит через submitMethod: валидацию формы и resolve() делает VDialogV3.
     * Здесь остаётся только доменная проверка уникальности (диалог про неё не знает)
     * и создание счёта.
     */
    async submit(): Promise<boolean> {
      if (this.localBankAccount.isAccountDuplicate() === true) {
        this.$core.showNotification.error(
          `Ошибка уникальности. Расчётный счёт с указанными юр. лицом, номером счёта и ${this.localBankAccount.getBankIdLabel()} уже существует.`
        );

        return false;
      }
      else {
        return await BankAccountsService.add(
          this.localBankAccount.company.id,
          this.localBankAccount,
        );
      }
    },
  },
});
</script>